import {
  convertPostgrestDateStringToLocalTime,
  FetchCategoryEpisodesParams,
  fetchCategoryEpisodesRequest,
  fetchCPACanadaEpisodeRequest,
  FetchEpisodeParams,
  fetchEpisodeRequest,
  FetchEpisodesParams,
  fetchEpisodesRequest,
  fetchEpisodesScheduledForArchiving,
  fetchFeaturedEpisodesRequest,
  FetchLibraryEpisodesParams,
  fetchLibraryEpisodesRequest,
  FetchNewEpisodesParams,
  fetchNewEpisodesRequest,
  fetchOrganizationPlaylistEpisodes,
  fetchRecommendedEpisodesRequest,
  fetchRelatedEpisodesRequest,
  fetchTrendingEpisodesRequest,
} from '@/requests';
import {
  ComplianceType,
  CPACanadaEpisode,
  Episode,
  EpisodeCard,
} from '@/types';
import { camelCaseKeys } from '@/utils';

import { selectEpisodeCompliance } from '../player-fsm-api';
import { postgrestApi } from '../postgrest-api';
import { RootState } from '../store';
import { PostgrestApiTags } from '../store.constants';
import {
  reorderEpisodes,
  transformEpisode,
  transformEpisodeCard,
} from './episodes-api.utils';

export const episodesApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchFeaturedEpisodes: builder.query<
      EpisodeCard[],
      Parameters<typeof fetchFeaturedEpisodesRequest>[0]
    >({
      query: fetchFeaturedEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
      providesTags: [PostgrestApiTags.FEATURED_EPISODES],
    }),
    fetchTrendingEpisodes: builder.query<
      EpisodeCard[],
      Parameters<typeof fetchTrendingEpisodesRequest>[0]
    >({
      query: fetchTrendingEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
      providesTags: [PostgrestApiTags.TRENDING_EPISODES],
    }),
    fetchNewEpisodes: builder.query<EpisodeCard[], FetchNewEpisodesParams>({
      query: fetchNewEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
      providesTags: [PostgrestApiTags.NEW_EPISODES],
    }),
    fetchLibraryEpisodes: builder.query<
      EpisodeCard[],
      FetchLibraryEpisodesParams
    >({
      query: fetchLibraryEpisodesRequest,
      transformResponse: (response: any, meta, args) =>
        (response as unknown[]).map(e =>
          transformEpisodeCard(e, args.compliance),
        ),
    }),
    fetchCategoryEpisodes: builder.query<
      EpisodeCard[],
      FetchCategoryEpisodesParams
    >({
      query: fetchCategoryEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
      providesTags: [PostgrestApiTags.CATEGORY_EPISODES],
    }),
    fetchEpisode: builder.query<Episode | undefined, FetchEpisodeParams>({
      query: fetchEpisodeRequest,
      transformResponse: (response: any) =>
        response?.[0] ? transformEpisode(response[0]) : undefined,
    }),
    fetchEpisodes: builder.query<EpisodeCard[], FetchEpisodesParams>({
      queryFn: async (params, { getState }, extraOptions, baseQuery) => {
        const { data, error } = await baseQuery(fetchEpisodesRequest(params));
        if (error) return { error };

        const episodes = (data as unknown[]).map(e => transformEpisodeCard(e));
        const { episodeIds, compliance: userCompliance } = params;
        if (episodeIds.length === episodes.length) {
          // Return episodes in the order of supplied episodeIds
          return {
            data: reorderEpisodes(episodeIds, episodes),
          };
        }

        // Determine which episodes are missing
        const state = getState() as RootState;
        const ids = episodes.map(({ episodeId }) => episodeId);
        const missingEpisodes = episodeIds
          .filter(episodeId => !ids.includes(episodeId))
          .reduce(
            (acc, episodeId) => {
              const episodeCompliance = selectEpisodeCompliance(
                state,
                episodeId,
              );
              if (episodeCompliance === userCompliance) {
                // If the episode is in the same compliance type, we don't try to fetch it again
                return acc;
              }

              return {
                ...acc,
                [episodeCompliance]: (acc[episodeCompliance] || []).concat(
                  episodeId,
                ),
              };
            },
            {} as Record<ComplianceType, string[]>,
          );

        // Compile list of episodes across all regions
        const supplementalEpisodes = await Promise.all(
          Object.entries(missingEpisodes).map(
            ([episodeComplianceType, supplementalIds]) =>
              baseQuery(
                fetchEpisodesRequest({
                  ...params,
                  episodeIds: supplementalIds,
                  compliance: episodeComplianceType as ComplianceType,
                }),
              ),
          ),
        );
        const fullEpisodes: EpisodeCard[] = supplementalEpisodes.reduce(
          (acc, { data, error }) => {
            if (error) return acc; // If an error occurs, we skip this region
            return acc.concat(
              (data as unknown[]).map(e => transformEpisodeCard(e)),
            );
          },
          episodes,
        );

        // Return episodes in the order of supplied episodeIds
        return {
          data: reorderEpisodes(episodeIds, fullEpisodes),
        };
      },
    }),
    fetchOrganizationPlaylistEpisodes: builder.query<
      EpisodeCard[],
      Parameters<typeof fetchOrganizationPlaylistEpisodes>[0]
    >({
      query: fetchOrganizationPlaylistEpisodes,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
      providesTags: [PostgrestApiTags.ORGANIZATION_PLAYLIST_EPISODES],
    }),
    fetchRecommendedEpisodes: builder.query<
      EpisodeCard[],
      Parameters<typeof fetchRecommendedEpisodesRequest>[0]
    >({
      query: fetchRecommendedEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
    }),
    fetchRelatedEpisodes: builder.query<
      EpisodeCard[],
      Parameters<typeof fetchRelatedEpisodesRequest>[0]
    >({
      query: fetchRelatedEpisodesRequest,
      transformResponse: (response: any) =>
        (response as unknown[]).map(e => transformEpisodeCard(e)),
    }),
    fetchEpisodeScheduledArchivalDate: builder.query<
      string | null,
      { episodeId: string }
    >({
      query: fetchEpisodesScheduledForArchiving,
      transformResponse: (response: any[]) =>
        response?.[0]?.scheduled_archiving_date
          ? convertPostgrestDateStringToLocalTime(
              response?.[0]?.scheduled_archiving_date,
            )
          : null,
    }),
    fetchCPACanadaEpisode: builder.query<CPACanadaEpisode | null, void>({
      query: fetchCPACanadaEpisodeRequest,
      transformResponse: (response: any[]) => {
        const cpaCanadaContent = response[0]?.cpa_canada_episode;

        return cpaCanadaContent
          ? camelCaseKeys<CPACanadaEpisode>({
              ...cpaCanadaContent,
              episode: {
                name: cpaCanadaContent.episode.title,
                supplemental: cpaCanadaContent.episode.non_verifiable_text,
                cover: cpaCanadaContent.episode.share_cover,
                companyLogo: cpaCanadaContent.header.logo,
                speakerBio: cpaCanadaContent.speaker.bio.replace(
                  /\\n/g,
                  '  \n',
                ),
                speakers: [
                  {
                    companyLogo: cpaCanadaContent.header.logo,
                    companyName: cpaCanadaContent.speaker.company,
                    ...cpaCanadaContent.speaker,
                  },
                ],
                takeaways: cpaCanadaContent.episode.key_takeaways
                  .split('\\n\\n')
                  .filter(
                    (takeaway: any) => !takeaway.includes('Learning Objective'),
                  ),
                ...cpaCanadaContent.episode,
                description: cpaCanadaContent.episode.description
                  .replace(/\\n/g, '  \n')
                  .replace(/&nbsp/g, ' '),
                glossary: cpaCanadaContent.episode.glossary
                  .split('\\n\\n')
                  .map((item: any) => {
                    const [_summary, term, definition] =
                      /\**-(.*):\s*\**\s*(.*)/.exec(item) || [];
                    return { term, definition };
                  }),
              },
              chapter: {
                name: cpaCanadaContent.episode.hook,
                duration: cpaCanadaContent.episode.duration,
              },
            })
          : null;
      },
    }),
  }),
});

export const {
  useFetchFeaturedEpisodesQuery,
  useFetchTrendingEpisodesQuery,
  useFetchLibraryEpisodesQuery,
  useFetchCategoryEpisodesQuery,
  useFetchEpisodeQuery,
  useFetchEpisodesQuery,
  useFetchRecommendedEpisodesQuery,
  useFetchRelatedEpisodesQuery,
  useFetchCPACanadaEpisodeQuery,
  useFetchNewEpisodesQuery,
  useFetchOrganizationPlaylistEpisodesQuery,
  useFetchEpisodeScheduledArchivalDateQuery,
} = episodesApi;
